import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { get, post } from '../../helpers/apiHelper';
import EmbeddedRegistrationForm from '../../components/embedded/EmbeddedRegistrationForm';

const EmbedRegistroPage = () => {
    const [searchParams] = useSearchParams();
    const code = searchParams.get('code');
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const loadConfig = async () => {
            if (!code) {
                setError('Código no proporcionado');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                // Intercambiar código por token y obtener configuración
                const response = await get(`clientes/embed/config?code=${code}`);

                // Manejar diferentes formatos de respuesta del backend
                let configData = null;

                // Formato esperado según documentación: { success: true, data: { config: {...} } }
                if (response.success && response.data && response.data.config) {
                    configData = response.data.config;
                }
                // Formato alternativo: respuesta directa con datos de configuración
                else if (response.tieneMarca || response.marcaId) {
                    // El backend devolvió la configuración directamente
                    // Necesitamos transformarla al formato esperado
                    configData = {
                        marca: {
                            id: response.marcaId,
                            nombre: response.marcaNombre,
                            puntos: response.marcaPuntos || 100,
                            puntosTicket: response.marcaPuntosTicket || 10
                        },
                        tienda: response.tieneTienda && response.tiendaId ? {
                            id: response.tiendaId,
                            nombre: response.tiendaNombre
                        } : undefined,
                        tiendas: response.cantidadTiendas > 0 ? [{
                            id: response.tiendaId,
                            nombre: response.tiendaNombre || response.primeraTienda
                        }] : [],
                        theme: response.theme || 'light',
                        lang: response.lang || 'es',
                        allowedOrigins: response.allowedOrigins || []
                    };
                }
                // Si no hay datos válidos
                else {
                    setError(response.error || response.message || 'Formato de respuesta no reconocido');
                    return;
                }

                if (configData) {
                    setConfig(configData);
                } else {
                    setError(response.error || response.message || 'Código inválido o expirado');
                }
            } catch (err) {
                console.error('Error loading embed config:', err);
                // Manejar diferentes tipos de errores
                if (err.status === 404 || (err.message && err.message.includes('404') || err.message.includes('Not Found'))) {
                    setError('Código de embed no encontrado. Verifica que el código sea correcto.');
                } else if (err.status === 401 || (err.message && (err.message.includes('401') || err.message.includes('Unauthorized')))) {
                    setError('Código inválido o expirado. Genera un nuevo código desde tu panel.');
                } else if (err.status === 403 || (err.message && (err.message.includes('403') || err.message.includes('Forbidden')))) {
                    // Error 403: El backend está rechazando la petición
                    // Si hay detalles del backend, usarlos; si no, mostrar mensaje genérico
                    if (err.details && err.details.message) {
                        // El backend devolvió detalles específicos sobre el error
                        let errorMsg = err.details.message;
                        if (err.details.troubleshooting && Array.isArray(err.details.troubleshooting)) {
                            errorMsg += '\n\n' + err.details.troubleshooting.map((tip, i) => `${i + 1}. ${tip}`).join('\n');
                        }
                        setError(errorMsg);
                    } else {
                        // Mensaje genérico si no hay detalles
                        setError('Acceso denegado por el servidor. Esto puede deberse a: 1) Validación de origen fallida, 2) El endpoint requiere configuración en el backend, 3) Problemas de CORS. Contacta con soporte técnico.');
                    }
                } else {
                    setError(err.message || 'Error al cargar la configuración del formulario');
                }
            } finally {
                setLoading(false);
            }
        };

        loadConfig();
    }, [code]);

    useEffect(() => {
        // Auto-resize del iframe (SEGURO)
        const updateHeight = () => {
            if (window.parent !== window) {
                const height = document.body.scrollHeight;
                // Obtener origen del padre desde referrer (más seguro que '*')
                const parentOrigin = document.referrer
                    ? new URL(document.referrer).origin
                    : '*';
                window.parent.postMessage({
                    type: 'resize',
                    height: height
                }, parentOrigin);
            }
        };

        // Actualizar altura inicial
        updateHeight();

        // Actualizar cuando cambie el contenido
        const observer = new MutationObserver(updateHeight);
        observer.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true
        });

        // Actualizar en resize
        window.addEventListener('resize', updateHeight);

        return () => {
            observer.disconnect();
            window.removeEventListener('resize', updateHeight);
        };
    }, [config]);

    const handleSuccess = (result) => {
        // El componente ya ha registrado el cliente, solo emitir evento de éxito (SEGURO)
        if (window.parent !== window) {
            const parentOrigin = document.referrer
                ? new URL(document.referrer).origin
                : '*';
            // Enviar solo datos mínimos, no información sensible
            window.parent.postMessage({
                type: 'success',
                payload: {
                    id: result.id,
                    codigoReferido: result.codigoReferido
                }
            }, parentOrigin);
        }
    };

    const handleError = (error) => {
        // Emitir evento de error al parent window (SEGURO)
        if (window.parent !== window) {
            const parentOrigin = document.referrer
                ? new URL(document.referrer).origin
                : '*';
            window.parent.postMessage({
                type: 'error',
                error: error.message || 'Error en el formulario'
            }, parentOrigin);
        }
    };

    if (loading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                fontFamily: 'system-ui, sans-serif'
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: '40px',
                        height: '40px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid #3B82F6',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        margin: '0 auto 1rem'
                    }}></div>
                    <p style={{ color: '#6B7280' }}>Cargando formulario...</p>
                </div>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    if (error) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                fontFamily: 'system-ui, sans-serif',
                padding: '2rem'
            }}>
                <div style={{
                    textAlign: 'center',
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                    padding: '2rem',
                    maxWidth: '500px'
                }}>
                    <h3 style={{ color: '#DC2626', marginBottom: '1rem' }}>Error</h3>
                    <p style={{ color: '#991B1B' }}>{error}</p>
                </div>
            </div>
        );
    }

    if (!config) {
        return null;
    }

    // Validar que la configuración sea válida según documentación
    if (!config.marca || !config.marca.id) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                fontFamily: 'system-ui, sans-serif',
                padding: '2rem'
            }}>
                <div style={{
                    textAlign: 'center',
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                    padding: '2rem',
                    maxWidth: '500px'
                }}>
                    <h3 style={{ color: '#DC2626', marginBottom: '1rem' }}>Error</h3>
                    <p style={{ color: '#991B1B' }}>Error al cargar la información de la marca</p>
                </div>
            </div>
        );
    }

    // Manejar tienda de forma segura según documentación
    // config.tienda puede ser undefined, pero config.tiendas SIEMPRE existe (array, puede estar vacío)
    const tiendas = config.tiendas || [];
    const tiendaId = config.tienda?.id || (tiendas.length > 0 ? tiendas[0].id : null);

    if (!tiendaId && tiendas.length === 0) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '400px',
                fontFamily: 'system-ui, sans-serif',
                padding: '2rem'
            }}>
                <div style={{
                    textAlign: 'center',
                    backgroundColor: '#FEF2F2',
                    border: '1px solid #FECACA',
                    borderRadius: '8px',
                    padding: '2rem',
                    maxWidth: '500px'
                }}>
                    <h3 style={{ color: '#DC2626', marginBottom: '1rem' }}>Error</h3>
                    <p style={{ color: '#991B1B' }}>Error al cargar la información de la tienda</p>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: '#ffffff',
            fontFamily: 'system-ui, -apple-system, sans-serif',
            lineHeight: '1.6',
            padding: '0',
            margin: '0'
        }}>
            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
                
                /* Reset básico para evitar conflictos con CSS del sitio host */
                * {
                    box-sizing: border-box;
                }
                
                body {
                    margin: 0;
                    padding: 0;
                }
                
                /* Asegurar que el formulario sea responsive */
                .embedded-registration-form {
                    max-width: 100%;
                    overflow-x: hidden;
                }
                
                /* Estilos para inputs en diferentes navegadores */
                input[type="text"],
                input[type="email"],
                input[type="date"],
                input[type="number"],
                select,
                textarea {
                    -webkit-appearance: none;
                    -moz-appearance: none;
                    appearance: none;
                }
                
                /* Checkboxes y radio buttons deben mantener su apariencia nativa */
                input[type="checkbox"],
                input[type="radio"] {
                    -webkit-appearance: checkbox;
                    -moz-appearance: checkbox;
                    appearance: checkbox;
                    cursor: pointer;
                }
                
                /* Mejorar accesibilidad */
                input:focus, select:focus, textarea:focus {
                    outline: 2px solid #3B82F6;
                    outline-offset: 2px;
                }
            `}</style>

            <EmbeddedRegistrationForm
                tiendaId={tiendaId}
                embedCode={config.marca.id}
                customStyles={config.customStyles || {}}
                theme={config.theme || 'light'}
                onSuccess={handleSuccess}
                onError={handleError}
                showHeader={config.showHeader !== false}
                showBenefits={config.showBenefits !== false}
                className="embed-registro-form"
                code={code}
                marcaId={config.marca.id}
                tiendaData={config.tienda || (tiendas.length > 0 ? tiendas[0] : null)}
            />
        </div>
    );
};

export default EmbedRegistroPage;

